import axios, { type AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';

// Расширяем axios config — добавляем счётчик попыток для retry logic
interface RetriableConfig extends InternalAxiosRequestConfig {
  __retryCount?: number;
  __retryStart?: number;
}

// ─── Retry конфигурация ──────────────────────────────────────────────────────

const RETRY_MAX_ATTEMPTS = 3;            // первая + 3 повтора = 4 попытки максимум
const RETRY_BASE_DELAY_MS = 400;         // 400ms → 800ms → 1600ms (exponential backoff)
const RETRY_TIMEOUT_TOTAL_MS = 32000;    // суммарно ≤ 32 секунд (быстрее чем было 45с)

// HTTP методы для которых retry безопасен (idempotent).
// POST/PATCH/PUT не повторяем — может создаться дубль заказа.
const RETRY_SAFE_METHODS = new Set(['get', 'head', 'options']);

// Endpoints где retry НЕ нужен (специфичные для авторизации / однократных действий).
// /admin/ добавлен после аварии с аналитикой: админские запросы тяжёлые, и когда
// база уже задыхается, повторы её только добивают. Витрину и мобилку это не
// затрагивает — там ретрай 5xx по-прежнему полезен.
const RETRY_SKIP_PATHS = [/^\/auth\//, /\/log\//, /\/logout/, /\/login/, /^\/admin\//];

function isRetriableError(error: AxiosError): boolean {
  const method = (error.config?.method || '').toLowerCase();
  if (!RETRY_SAFE_METHODS.has(method)) return false;

  const url = error.config?.url || '';
  if (RETRY_SKIP_PATHS.some((re) => re.test(url))) return false;

  // 1. Network error без response (DNS / TCP / TLS / нет ответа)
  if (!error.response) {
    // ECONNABORTED = таймаут axios. ERR_NETWORK = сеть отвалилась.
    if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || error.message?.includes('timeout')) {
      return true;
    }
    if (error.code === 'ERR_CANCELED') return false; // отмена через AbortController — не повторяем
    return true; // прочие network errors — пытаемся
  }

  // 2. Серверные ошибки 5xx + 429 (rate limit)
  const status = error.response.status;
  if (status === 429 || (status >= 500 && status < 600)) return true;

  return false;
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// Create axios instance with base configuration
export const api = axios.create({
  // Всегда используем локальный прокси /api для передачи cookies
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  // 8 секунд на одну попытку. Раньше было 15s — но на iOS Safari при уходе
  // tab в background, наш fetch висит до самого timeout, потом ВСЕ запросы
  // одновременно фейлятся (мы это видели в логах). С 8s + 4 попытками
  // exp backoff = клиент быстрее получит retry (если возврат к tab в моменте).
  // С retry max 3 = до 32 секунд суммарно (см. RETRY_TIMEOUT_TOTAL_MS).
  timeout: 8000,
  withCredentials: true, // Важно для передачи cookies
});

// Request interceptor - add auth token + засекаем start time
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = Cookies.get('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const c = config as RetriableConfig;
    if (c.__retryStart == null) c.__retryStart = Date.now();
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor — auth + retry logic
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      Cookies.remove('accessToken');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }
    }

    // ─── Auto-retry для transient ошибок ──────────────────────────────────
    // Чинит 80% "мерцающих" сетевых проблем у клиентов:
    //   - TCP reset / lost packets на мобильном
    //   - Случайные 502/503 от бэка под нагрузкой
    //   - Кратковременные DNS issues
    //   - 429 rate limiting (с backoff)
    //
    // ОГРАНИЧЕНИЯ:
    //   - Только GET/HEAD/OPTIONS (idempotent) — чтобы не дублировать заказы
    //   - Максимум 2 повтора (3 попытки всего)
    //   - Суммарное время не более 45 секунд
    const config = error.config as RetriableConfig | undefined;
    if (config && isRetriableError(error)) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      const elapsedMs = Date.now() - (config.__retryStart || Date.now());

      if (config.__retryCount <= RETRY_MAX_ATTEMPTS && elapsedMs < RETRY_TIMEOUT_TOTAL_MS) {
        const waitMs = RETRY_BASE_DELAY_MS * Math.pow(2, config.__retryCount - 1);
        if (typeof console !== 'undefined') {
          console.warn(
            `[API retry ${config.__retryCount}/${RETRY_MAX_ATTEMPTS}] ${config.method?.toUpperCase()} ${config.url} ` +
              `failed (${error.code || error.message}). Retry in ${waitMs}ms…`
          );
        }
        await delay(waitMs);
        return api(config as AxiosRequestConfig);
      }
    }

    // Log network errors в dev console (полный error-tracker ловит через XHR-патч)
    if (!error.response) {
      const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
      const url = error.config?.url || 'unknown-url';
      const attempts = (error.config as RetriableConfig | undefined)?.__retryCount ?? 0;
      console.error(
        `Network Error ${method} ${url} (after ${attempts} retries): ${error.message || error.code || 'unknown'}`
      );
    }

    return Promise.reject(error);
  }
);

// API helper types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Generic API methods
export const apiClient = {
  get: async <T>(url: string, params?: object): Promise<T> => {
    const response = await api.get<T>(url, { params });
    return response.data;
  },

  post: async <T>(url: string, data?: object): Promise<T> => {
    const response = await api.post<T>(url, data);
    return response.data;
  },

  put: async <T>(url: string, data?: object): Promise<T> => {
    const response = await api.put<T>(url, data);
    return response.data;
  },

  patch: async <T>(url: string, data?: object): Promise<T> => {
    const response = await api.patch<T>(url, data);
    return response.data;
  },

  delete: async <T>(url: string): Promise<T> => {
    const response = await api.delete<T>(url);
    return response.data;
  },
};

export default api;
